import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Grid,
  Text,
  Badge,
  Divider,
  Avatar,
  Tabs,
  ActionIcon,
  Tooltip,
  Menu,
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconDots,
  IconArrowLeft,
  IconShare,
  IconPrinter,
  IconDownload,
} from '@tabler/icons-react';
import { Button, Card, Title } from '../../ui';
import { FadeIn, SlideIn } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface DetailField {
  key: string;
  label: string;
  render?: (value: unknown) => ReactNode;
  span?: number;
  type?: 'text' | 'badge' | 'date' | 'currency' | 'link' | 'email' | 'phone' | 'custom';
  badgeColor?: string;
  copyable?: boolean;
  hidden?: boolean;
}

export interface DetailSection {
  key: string;
  title: string;
  icon?: ReactNode;
  fields: DetailField[];
}

export interface DetailTab {
  key: string;
  label: string;
  icon?: ReactNode;
  content: ReactNode;
  badge?: number | string;
}

export interface EntityDetailProps<T extends Record<string, unknown>> {
  data: T;
  title?: string | ((data: T) => string);
  subtitle?: string | ((data: T) => ReactNode);
  avatar?: string | ((data: T) => string);
  status?: {
    value: string;
    color: string;
  };
  sections?: DetailSection[];
  tabs?: DetailTab[];
  actions?: {
    onEdit?: () => void;
    onDelete?: () => void;
    onBack?: () => void;
    custom?: {
      key: string;
      label: string;
      icon?: ReactNode;
      onClick: () => void;
      color?: string;
    }[];
  };
  headerExtra?: ReactNode;
  sideContent?: ReactNode;
  loading?: boolean;
  className?: string;
}

export const EntityDetail = forwardRef<HTMLDivElement, EntityDetailProps<Record<string, unknown>>>(
  (
    {
      data,
      title,
      subtitle,
      avatar,
      status,
      sections = [],
      tabs = [],
      actions,
      headerExtra,
      sideContent,
      loading = false,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const resolvedTitle = typeof title === 'function' ? title(data) : title;
    const resolvedSubtitle = typeof subtitle === 'function' ? subtitle(data) : subtitle;
    const resolvedAvatar = typeof avatar === 'function' ? avatar(data) : avatar;

    const getValue = (key: string): unknown => {
      const keys = key.split('.');
      let value: unknown = data;
      for (const k of keys) {
        value = (value as Record<string, unknown>)?.[k];
      }
      return value;
    };

    const formatValue = (field: DetailField, value: unknown): ReactNode => {
      if (field.render) {
        return field.render(value);
      }

      if (value === null || value === undefined) {
        return <Text c="dimmed" style={accessibilityStyles}>-</Text>;
      }

      switch (field.type) {
        case 'badge':
          return <Badge color={field.badgeColor}>{String(value)}</Badge>;

        case 'date':
          return (
            <Text style={accessibilityStyles}>
              {value instanceof Date
                ? value.toLocaleDateString()
                : new Date(String(value)).toLocaleDateString()}
            </Text>
          );

        case 'currency':
          return (
            <Text fw={500} style={accessibilityStyles}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(Number(value))}
            </Text>
          );

        case 'link':
          return (
            <Text
              component="a"
              href={String(value)}
              target="_blank"
              c="blue"
              style={{ ...accessibilityStyles, textDecoration: 'underline' }}
            >
              {String(value)}
            </Text>
          );

        case 'email':
          return (
            <Text component="a" href={`mailto:${value}`} c="blue" style={accessibilityStyles}>
              {String(value)}
            </Text>
          );

        case 'phone':
          return (
            <Text component="a" href={`tel:${value}`} c="blue" style={accessibilityStyles}>
              {String(value)}
            </Text>
          );

        default:
          return <Text style={accessibilityStyles}>{String(value)}</Text>;
      }
    };

    const renderFields = (fields: DetailField[]) => (
      <Grid>
        {fields
          .filter((field) => !field.hidden)
          .map((field) => (
            <Grid.Col key={field.key} span={{ base: 12, sm: field.span || 6 }}>
              <Stack gap={4}>
                <Text size="sm" c="dimmed" style={accessibilityStyles}>{field.label}</Text>
                {formatValue(field, getValue(field.key))}
              </Stack>
            </Grid.Col>
          ))}
      </Grid>
    );

    return (
      <FadeIn>
        <Box ref={ref} className={className}>
          <Stack gap="lg">
            {/* Header */}
            <Card>
              <Group justify="space-between" align="flex-start">
                <Group>
                  {actions?.onBack && (
                    <ActionIcon variant="subtle" onClick={actions.onBack} size="lg">
                      <IconArrowLeft size={20} />
                    </ActionIcon>
                  )}
                  {resolvedAvatar && (
                    <Avatar src={resolvedAvatar} size="xl" radius="md" />
                  )}
                  <Box>
                    <Group gap="sm">
                      {resolvedTitle && <Title order={2}>{resolvedTitle}</Title>}
                      {status && <Badge color={status.color}>{status.value}</Badge>}
                    </Group>
                    {resolvedSubtitle && (
                      <Text size="sm" c="dimmed" mt={4} style={accessibilityStyles}>
                        {resolvedSubtitle}
                      </Text>
                    )}
                  </Box>
                </Group>
                <Group gap="xs">
                  {headerExtra}
                  <Tooltip label="Share">
                    <ActionIcon variant="subtle">
                      <IconShare size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Print">
                    <ActionIcon variant="subtle">
                      <IconPrinter size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Download">
                    <ActionIcon variant="subtle">
                      <IconDownload size={18} />
                    </ActionIcon>
                  </Tooltip>
                  {actions?.onEdit && (
                    <Button
                      variant="subtle"
                      leftSection={<IconEdit size={16} />}
                      onClick={actions.onEdit}
                    >
                      Edit
                    </Button>
                  )}
                  {(actions?.onDelete || actions?.custom?.length) && (
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle">
                          <IconDots size={18} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {actions.custom?.map((action) => (
                          <Menu.Item
                            key={action.key}
                            leftSection={action.icon}
                            color={action.color}
                            onClick={action.onClick}
                          >
                            {action.label}
                          </Menu.Item>
                        ))}
                        {actions.onDelete && (
                          <>
                            <Menu.Divider />
                            <Menu.Item
                              leftSection={<IconTrash size={16} />}
                              color="red"
                              onClick={actions.onDelete}
                            >
                              Delete
                            </Menu.Item>
                          </>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Group>
            </Card>

            {/* Main Content */}
            <Grid>
              <Grid.Col span={{ base: 12, md: sideContent ? 8 : 12 }}>
                {tabs.length > 0 ? (
                  <Card>
                    <Tabs defaultValue={tabs[0]?.key}>
                      <Tabs.List>
                        {tabs.map((tab) => (
                          <Tabs.Tab
                            key={tab.key}
                            value={tab.key}
                            leftSection={tab.icon}
                            rightSection={
                              tab.badge !== undefined ? (
                                <Badge size="xs" variant="filled" radius="xl">
                                  {tab.badge}
                                </Badge>
                              ) : undefined
                            }
                          >
                            {tab.label}
                          </Tabs.Tab>
                        ))}
                      </Tabs.List>
                      {tabs.map((tab) => (
                        <Tabs.Panel key={tab.key} value={tab.key} pt="md">
                          <SlideIn direction="up" delay={100}>
                            {tab.content}
                          </SlideIn>
                        </Tabs.Panel>
                      ))}
                    </Tabs>
                  </Card>
                ) : (
                  <Stack gap="lg">
                    {sections.map((section) => (
                      <Card key={section.key}>
                        <Stack gap="md">
                          <Group gap="sm">
                            {section.icon}
                            <Text fw={600} style={accessibilityStyles}>{section.title}</Text>
                          </Group>
                          <Divider />
                          {renderFields(section.fields)}
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Grid.Col>

              {sideContent && (
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <SlideIn direction="right" delay={200}>
                    {sideContent}
                  </SlideIn>
                </Grid.Col>
              )}
            </Grid>
          </Stack>
        </Box>
      </FadeIn>
    );
  }
);

EntityDetail.displayName = 'EntityDetail';

export default EntityDetail;
